import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../api/client';
import { Logo } from '../components/Logo';

// The words that step across the demo caption. Kept short so the "Punch" look
// (uppercase, one bright active word at a time) reads instantly.
const DEMO_WORDS = ['MAKE', 'EVERY', 'WORD', 'HIT'];

export function LoginPage() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const register = mode === 'register';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (register) await api.register(email, password);
      else await api.login(email, password);
      nav('/');
    } catch (err) {
      setError(
        err instanceof ApiClientError ? err.message : 'Request failed (is the API running?)',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth2">
      {/* Left: the product showing off. The login page captions itself. */}
      <aside className="auth2-show">
        <div className="auth2-show-inner">
          <div className="auth2-preview" aria-hidden="true">
            <div className="auth2-frame">
              <div className="auth2-caption">
                {DEMO_WORDS.map((w, i) => (
                  <span key={w} className="auth2-word" style={{ ['--i' as string]: i }}>
                    {w}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="auth2-show-copy">
            <h2 className="auth2-headline">
              Every word,
              <br />
              <span>right on the beat.</span>
            </h2>
            <ul className="auth2-points">
              <li>Transcribes and word-syncs the clip for you</li>
              <li>Six styles: Punch, Mega, Spotlight, and more</li>
              <li>kMagic art-directs the whole edit</li>
            </ul>
          </div>
        </div>
      </aside>

      {/* Right: the form. */}
      <main className="auth2-panel">
        <form className="auth2-card" onSubmit={submit}>
          <div className="auth2-brand">
            <Logo size={22} />
          </div>
          <h1 className="auth2-title">{register ? 'Create an account' : 'Sign in'}</h1>
          <p className="auth2-sub">
            {register ? 'No card required.' : 'Sign in to your Kaption account.'}
          </p>

          <label className="auth2-field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              placeholder="you@studio.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="auth2-field">
            <span>Password</span>
            <input
              type="password"
              autoComplete={register ? 'new-password' : 'current-password'}
              placeholder={register ? 'At least 10 characters' : '••••••••••'}
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {error && <p className="auth2-error">{error}</p>}

          <button className="auth2-submit" disabled={busy}>
            {busy ? (register ? 'Creating…' : 'Signing in…') : register ? 'Create account' : 'Sign in'}
          </button>

          <p className="auth2-switch">
            {register ? 'Already have an account?' : 'New to Kaption?'}{' '}
            <button type="button" onClick={() => setMode(register ? 'login' : 'register')}>
              {register ? 'Sign in' : 'Create one'}
            </button>
          </p>
        </form>
      </main>
    </div>
  );
}
